import axios from "axios";
import eventBus, { EventTypes } from "../services/eventBus";

// In production, use relative URL to work with any domain (Heroku review apps, staging, production)
// In development, use the proxy defined in package.json or localhost
const API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "/api" // Relative URL - works with any domain
    : process.env.REACT_APP_API_URL || "http://localhost:8000/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests if available (skip for public endpoints)
// Note: Tokens are now stored in httpOnly cookies and sent automatically by the browser
// This interceptor is kept for backwards compatibility with agent tokens
api.interceptors.request.use(
  (config) => {
    // List of public endpoints that don't require authentication
    const publicEndpoints = [
      "/auth/login",
      "/auth/register",
      "/auth/coach-signup",
      "/auth/forgot-password",
      "/auth/reset-password",
      "/auth/reset-password/validate",
      "/auth/agent-login/request",
      "/auth/agent-login/exchange",
      "/instructors/validate-token",
      "/instructors/validate-org-token",
      "/instructors/claim-class",
      "/classEvents/available-for-instructors",
    ];

    // Check if this is a public endpoint
    const isPublicEndpoint = publicEndpoints.some(
      (endpoint) => config.url && config.url.includes(endpoint)
    );

    if (!isPublicEndpoint) {
      config.headers = config.headers || {};
      const hasAuthorizationHeader = Boolean(
        config.headers && config.headers.Authorization
      );

      // Only check for agent token (backwards compatibility)
      // Coach tokens are now in httpOnly cookies
      const agentToken = localStorage.getItem("agentToken");

      if (!hasAuthorizationHeader && agentToken) {
        config.headers.Authorization = `Bearer ${agentToken}`;
      }
    }

    // Enable credentials to send cookies with requests
    config.withCredentials = true;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Storage for the plan upgrade handler (set by App.js)
let planUpgradeHandler = null;

/**
 * Set the handler for 403 plan upgrade errors
 * This is called from App.js after the PlanUpgradeContext is initialized
 * @param {Function} handler - Function to call when a 403 FEATURE_NOT_AVAILABLE error occurs
 */
export const setPlanUpgradeHandler = (handler) => {
  planUpgradeHandler = handler;
};

// Add response interceptor to handle 403 plan errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check if this is a plan-related 403 error
    if (
      error.response &&
      error.response.status === 403 &&
      error.response.data &&
      error.response.data.error === 'FEATURE_NOT_AVAILABLE' &&
      planUpgradeHandler
    ) {
      // Call the handler to show the upgrade modal
      planUpgradeHandler(error.response.data);
    }

    // Always reject the error so calling code can handle it too if needed
    return Promise.reject(error);
  }
);

export const agents = {
  getAll: (params = {}) => api.get("/agents", { params }),
  getById: (id) => api.get(`/agents/${id}`),
  create: (data) => api.post("/agents", data),
  update: (id, data) => api.put(`/agents/${id}`, data),
  delete: (id) => api.delete(`/agents/${id}`),
  getCommunications: (id) => api.get(`/agents/${id}/communications`),
  getTransactions: (id) => api.get(`/agents/${id}/transactions`),
  updateMilestone: (id, data) => api.put(`/agents/${id}/milestone`, data),
  updateMeetingAvailability: (id, data) =>
    api.put(`/agents/${id}/meeting-availability`, data),
  getTags: (params = {}) => api.get("/agents/tags", { params }),
  renameTag: (oldName, newName) =>
    api.post("/agents/tags/rename", { oldName, newName }),
  mergeTags: (sources, target) =>
    api.post("/agents/tags/merge", { sources, target }),
  deleteTag: (tag) => api.delete(`/agents/tags/${encodeURIComponent(tag)}`),
  getWeeklyCheckIns: (id, params = {}) =>
    api.get(`/agents/${id}/weekly-checkins`, { params }),
  getTaskOverview: (id, params = {}) =>
    api.get(`/agents/${id}/task-overview`, { params }),
  getEngagement: (id, params = {}) =>
    api.get(`/agents/${id}/engagement`, { params }),
  // Onboarding and bulk import
  getOnboardingStatus: () => api.get("/agents/onboarding/status"),
  bulkImport: (agents) => api.post("/agents/bulk-import", { agents }),
};

export const businessMath = {
  coach: {
    get: (agentId, year) => api.get(`/business-math/agents/${agentId}`, { params: { year } }),
    save: (agentId, data) => api.put(`/business-math/agents/${agentId}`, data),
  },
  agent: {
    get: (year) => api.get("/agent/business-math", { params: { year } }),
  },
};

export const apiAccessKeys = {
  list: () => api.get("/access-keys"),
  create: (data) => api.post("/access-keys", data),
  revoke: (id) => api.post(`/access-keys/${id}/revoke`),
  getAudit: (id) => api.get(`/access-keys/${id}/audit`),
};

export const pipeline = {
  getAll: (params = {}) => api.get("/transactions", { params }),
  getById: (id) => api.get(`/transactions/${id}`),
  create: (data) => api.post("/transactions", data),
  update: (id, data) => api.put(`/transactions/${id}`, data),
  updateStatus: (id, data) => api.put(`/transactions/${id}/status`, data),
  updateCalculations: (id, data) =>
    api.put(`/transactions/${id}/calculations`, data),
  addNote: (id, data) => api.post(`/transactions/${id}/notes`, data),
  delete: (id) => api.delete(`/transactions/${id}`),
  getCoordinators: () => api.get("/transactions/coordinators"),
};

// Keep transactions as alias for backwards compatibility (agent portal)
export const transactions = pipeline;

export const communications = {
  getAll: (params = {}) => api.get("/communications", { params }),
  getById: (id) => api.get(`/communications/${id}`),
  create: (data) => api.post("/communications", data),
  update: (id, data) => api.put(`/communications/${id}`, data),
  delete: (id) => api.delete(`/communications/${id}`),
  // Enhanced search and filtering
  search: (params = {}) => api.get("/communications/search", { params }),
  getFacets: (params = {}) => api.get("/communications/facets", { params }),
  getSuggestions: (params = {}) =>
    api.get("/communications/suggestions", { params }),
  getStats: (params = {}) => api.get("/communications/stats", { params }),
  // Bulk operations
  bulkDelete: (communicationIds) =>
    api.post("/communications/bulk/delete", { communicationIds }),
  bulkAddTags: (communicationIds, tags) =>
    api.post("/communications/bulk/tags/add", { communicationIds, tags }),
  bulkRemoveTags: (communicationIds, tags) =>
    api.post("/communications/bulk/tags/remove", { communicationIds, tags }),
  bulkFollowUp: (communicationIds, followUpData) =>
    api.post("/communications/bulk/follow-up", {
      communicationIds,
      ...followUpData,
    }),
  // Analytics endpoints
  analytics: {
    trends: (params = {}) =>
      api.get("/communications/analytics/trends", { params }),
    agentPerformance: (params = {}) =>
      api.get("/communications/analytics/agent-performance", { params }),
    tags: (params = {}) =>
      api.get("/communications/analytics/tags", { params }),
    patterns: (params = {}) =>
      api.get("/communications/analytics/patterns", { params }),
    export: (data) =>
      api.post("/communications/analytics/export", data, {
        responseType: "blob", // For file downloads
      }),
  },
};

export const dashboard = {
  getStats: () => api.get("/dashboard/stats"),
  getClosingsMonthly: () => api.get("/dashboard/closings-monthly"),
  getUnderContractMonthly: () => api.get("/dashboard/under-contract-monthly"),
  getAgentsNeedingFollowup: () => api.get("/dashboard/agents-needing-followup"),
  getRecentActivity: () => api.get("/dashboard/recent-activity"),
  getAtRiskQueue: () => api.get("/dashboard/at-risk-queue"),
};

export const agentRisk = {
  get: (agentId) => api.get(`/agent-risk/agents/${agentId}`),
  recalculate: (agentId) => api.post(`/agent-risk/agents/${agentId}/recalculate`),
  addContext: (agentId, data) => api.post(`/agent-risk/agents/${agentId}/contexts`, data),
  resolveContext: (agentId, contextId) => api.put(`/agent-risk/agents/${agentId}/contexts/${contextId}/resolve`),
};

export const reports = {
  getAgentsBehindMilestones: () => api.get("/reports/agents-behind-milestones"),
  getStaleTransactions: () => api.get("/reports/stale-transactions"),
  getFollowUpNeeded: () => api.get("/reports/follow-up-needed"),
  getPipelineHealth: () => api.get("/reports/pipeline-health"),
  getWeeklySummary: () => api.get("/reports/weekly-summary"),
};

export const classes = {
  getAll: (params = {}) => api.get("/classes", { params }),
  getById: (id) => api.get(`/classes/${id}`),
  create: (data) => api.post("/classes", data),
  update: (id, data) => api.put(`/classes/${id}`, data),
  delete: (id) => api.delete(`/classes/${id}`),
  getEvents: (id) => api.get(`/classes/${id}/events`),
};

export const classEvents = {
  getAll: (params = {}) => api.get("/classEvents", { params }),
  getById: (id) => api.get(`/classEvents/${id}`),
  create: (data) => api.post("/classEvents", data),
  update: (id, data) => api.put(`/classEvents/${id}`, data),
  delete: (id) => api.delete(`/classEvents/${id}`),
  bulkCreate: (data) => api.post("/classEvents/bulk-create", data),
  updateAttendance: (id, data) =>
    api.put(`/classEvents/${id}/attendance`, data),
  getGaps: (params) => api.get("/classEvents/analysis/gaps", { params }),
  getAvailableForInstructors: (params = {}) =>
    api.get("/classEvents/available-for-instructors", { params }),

  // Agent-specific endpoints
  agent: {
    getWeekly: (params = {}) =>
      api.get("/classEvents/agent/weekly", { params }),
    getTodayPowerUp: () => api.get("/classEvents/agent/power-up/today"),
    register: (eventId) => api.post("/classEvents/agent/register", { eventId }),
    unregister: (eventId) =>
      api.delete("/classEvents/agent/unregister", { data: { eventId } }),
  },
};

export const dailyCheckIns = {
  getGrid: (params = {}) => api.get('/daily-checkins/grid', { params }),
  getLeaderboard: (params = {}) => api.get('/daily-checkins/leaderboard', { params }),
  updateAttendance: (eventId, agentIds, attendanceStatus) =>
    api.put('/daily-checkins/attendance', { eventId, agentIds, attendanceStatus }),
  agent: {
    getToday: (date) => api.get('/daily-checkins/agent/today', { params: date ? { date } : {} }),
    saveToday: (data) => api.put('/daily-checkins/agent/today', data),
    getLeaderboard: (params = {}) => api.get('/daily-checkins/agent/leaderboard', { params }),
  },
};

export const seriesTemplates = {
  getAll: (params = {}) => api.get("/seriesTemplates", { params }),
  getById: (id) => api.get(`/seriesTemplates/${id}`),
  create: (data) => api.post("/seriesTemplates", data),
  update: (id, data) => api.put(`/seriesTemplates/${id}`, data),
  delete: (id) => api.delete(`/seriesTemplates/${id}`),
  generateSchedule: (id, data) =>
    api.post(`/seriesTemplates/${id}/generate-schedule`, data),
};

export const agentNotes = {
  getByAgent: (agentId, params = {}) =>
    api.get(`/agent-notes/agent/${agentId}`, { params }),
  create: (data) => api.post("/agent-notes", data),
  update: (id, data) => api.put(`/agent-notes/${id}`, data),
  delete: (id) => api.delete(`/agent-notes/${id}`),
};

export const superAdmin = {
  getUsage: (params = {}) => api.get("/super-admin/usage", { params }),
  getDashboard: (params = {}) => api.get("/super-admin/dashboard", { params }),
  getOrganizations: (params = {}) =>
    api.get("/super-admin/organizations", { params }),
  getOrganizationDetails: (orgId) => api.get(`/super-admin/organizations/${orgId}`),
  createOrganization: (data) => api.post("/super-admin/organizations", data),
  getUsers: (params = {}) => api.get("/super-admin/users", { params }),
  getUserDetails: (userId) => api.get(`/super-admin/users/${userId}`),
  createUser: (data) => api.post("/super-admin/users", data),
};

export const instructors = {
  getAll: (params = {}) => api.get("/instructors", { params }),
  getById: (id) => api.get(`/instructors/${id}`),
  create: (data) => api.post("/instructors", data),
  update: (id, data) => api.put(`/instructors/${id}`, data),
  delete: (id) => api.delete(`/instructors/${id}`),
  getStats: (id) => api.get(`/instructors/${id}/stats`),
  generateToken: (id) => api.post(`/instructors/${id}/generate-token`),
  generateClassSignupLink: () => api.post("/instructors/generate-class-signup-link"),
  getAvailable: (params) =>
    api.get("/instructors/available/search", { params }),
  bulkEmail: (data) => api.post("/instructors/bulk-email", data),
  validateToken: (token) => api.get(`/instructors/validate-token/${token}`),
  validateOrgToken: (token) => api.get(`/instructors/validate-org-token/${token}`),
  claimClass: (data) => api.post("/instructors/claim-class", data),
};

const emitTaskEvent = (eventType, payload) => {
  // Fire and forget so API consumers aren't blocked by event handlers
  eventBus.emit(eventType, payload, { source: "tasksApi" });
};

const normalizeTaskDueDate = (payload = {}) => {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  if (!Object.prototype.hasOwnProperty.call(payload, 'dueDate')) {
    return payload;
  }

  const normalizedPayload = { ...payload };
  const { dueDate } = normalizedPayload;

  if (dueDate === undefined) {
    delete normalizedPayload.dueDate;
    return normalizedPayload;
  }

  if (dueDate === null) {
    normalizedPayload.dueDate = null;
    return normalizedPayload;
  }

  if (typeof dueDate === 'string') {
    normalizedPayload.dueDate = dueDate.trim();
    return normalizedPayload;
  }

  const asDate = new Date(dueDate);
  if (Number.isNaN(asDate.getTime())) {
    return normalizedPayload;
  }

  normalizedPayload.dueDate = asDate.toISOString().split('T')[0];
  return normalizedPayload;
};

export const tasks = {
  getAll: (params = {}) => api.get("/tasks", { params }),
  getById: (id) => api.get(`/tasks/${id}`),
  create: (data) => {
    const payload = normalizeTaskDueDate(data);
    return api.post("/tasks", payload).then((response) => {
      emitTaskEvent(EventTypes.TASK_CREATED, response.data);
      return response;
    });
  },
  update: (id, data) => {
    const payload = normalizeTaskDueDate(data);
    return api.put(`/tasks/${id}`, payload).then((response) => {
      emitTaskEvent(EventTypes.TASK_UPDATED, { id, updates: payload, response: response.data });
      return response;
    });
  },
  delete: (id) =>
    api.delete(`/tasks/${id}`).then((response) => {
      emitTaskEvent(EventTypes.TASK_DELETED, { id });
      return response;
    }),
  complete: (id, actualHours) =>
    api.put(`/tasks/${id}/complete`, { actualHours }).then((response) => {
      emitTaskEvent(EventTypes.TASK_COMPLETED, { id, actualHours, response: response.data });
      return response;
    }),
  uncomplete: (id, status) =>
    api.put(`/tasks/${id}/uncomplete`, { status }).then((response) => {
      emitTaskEvent(EventTypes.TASK_UPDATED, { id, status, response: response.data });
      return response;
    }),
  reschedule: (id, dueDate) => {
    // Check if we're in agent context (agent token still uses localStorage for backwards compat)
    const agentToken = localStorage.getItem("agentToken");

    const normalizedPayload = normalizeTaskDueDate({ dueDate });
    const normalizedDueDate = normalizedPayload.dueDate;

    if (agentToken) {
      return api
        .put(
          `/agent-task-assignments/tasks/${id}/reschedule`,
          normalizedPayload
        )
        .then((response) => {
          emitTaskEvent(EventTypes.TASK_UPDATED, { id, dueDate: normalizedDueDate, response: response.data });
          return response;
        });
    }

    // For coach users, token is in httpOnly cookie
    return api
      .put(`/tasks/${id}/reschedule`, normalizedPayload)
      .then((response) => {
        emitTaskEvent(EventTypes.TASK_UPDATED, { id, dueDate: normalizedDueDate, response: response.data });
        return response;
      });
  },
  getByModuleEntity: (module, entityType, entityId) =>
    api.get(`/tasks/module/${module}/${entityType}/${entityId}`),
  getOverdue: () => api.get("/tasks/status/overdue"),
  getDueToday: () => api.get("/tasks/status/due-today"),
  getDueThisWeek: () => api.get("/tasks/status/due-this-week"),
  getHighPriority: () => api.get("/tasks/priority/high"),
  bulkUpdate: (taskIds, updates) =>
    api.put("/tasks/bulk", { taskIds, updates }).then((response) => {
      emitTaskEvent(EventTypes.TASK_UPDATED, { taskIds, updates, response: response.data });
      return response;
    }),
  // New endpoints for enhanced task management
  getAIAssigned: () => api.get("/tasks/ai-assigned"),
  getUpcoming: (days = 7) => api.get("/tasks/upcoming", { params: { days } }),
  getCompleted: (limit = 50) =>
    api.get("/tasks/completed", { params: { limit } }),
  assignToAI: (id, data) =>
    api.post(`/tasks/${id}/assign-to-ai`, data).then((response) => {
      emitTaskEvent(EventTypes.TASK_UPDATED, { id, updates: data, response: response.data });
      return response;
    }),
  updateAIProgress: (id, data) =>
    api.put(`/tasks/${id}/ai-progress`, data).then((response) => {
      emitTaskEvent(EventTypes.TASK_UPDATED, { id, updates: data, response: response.data });
      return response;
    }),
  stats: () => api.get("/tasks/stats"),
  // Task awareness endpoint for lightweight stats
  awareness: () => api.get("/tasks/awareness"),
};

// Recordings API
export const recordings = {
  transcribe: (formData) => {
    // Create a new axios instance without JSON headers for FormData
    const formDataApi = axios.create({
      baseURL: API_BASE_URL,
      withCredentials: true, // Enable cookies for auth
    });

    // Note: Auth token is now in httpOnly cookie, sent automatically
    // No need to add Authorization header manually

    return formDataApi.post("/recordings/transcribe", formData);
  },
  search: (params = {}) => api.get("/recordings/search", { params }),
};

export const blockedDays = {
  getAll: (params = {}) => api.get("/blocked-days", { params }),
  getById: (id) => api.get(`/blocked-days/${id}`),
  create: (data) => api.post("/blocked-days", data),
  update: (id, data) => api.put(`/blocked-days/${id}`, data),
  delete: (id) => api.delete(`/blocked-days/${id}`),
  bulkCreate: (data) => api.post("/blocked-days/bulk", data),
};

export const classResources = {
  getByEvent: (eventId) => api.get(`/class-resources/event/${eventId}`),
  create: (eventId, data) =>
    api.post(`/class-resources/event/${eventId}`, data),
  upload: (eventId, formData) =>
    api.post(`/class-resources/event/${eventId}/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  update: (id, data) => api.put(`/class-resources/${id}`, data),
  delete: (id) => api.delete(`/class-resources/${id}`),
  share: (data) => api.post("/class-resources/share", data),
  getShares: (eventId) => api.get(`/class-resources/shares/event/${eventId}`),
  trackView: (id) => api.post(`/class-resources/${id}/view`),
  trackDownload: (id) => api.post(`/class-resources/${id}/download`),
  getPublic: (accessToken) => api.get(`/class-resources/public/${accessToken}`),
};

export const communicationTemplates = {
  getAll: (params = {}) => api.get("/communication-templates", { params }),
  getById: (id) => api.get(`/communication-templates/${id}`),
  create: (data) => api.post("/communication-templates", data),
  update: (id, data) => api.put(`/communication-templates/${id}`, data),
  delete: (id) => api.delete(`/communication-templates/${id}`),
  preview: (id, variables) =>
    api.post(`/communication-templates/${id}/preview`, { variables }),
  apply: (id, data) => api.post(`/communication-templates/${id}/apply`, data),
  getCategories: () => api.get("/communication-templates/metadata/categories"),
  duplicate: (id) => api.post(`/communication-templates/${id}/duplicate`),
  getPopular: (limit = 10) =>
    api.get("/communication-templates/metadata/popular", { params: { limit } }),
  getAnalytics: (id) => api.get(`/communication-templates/${id}/analytics`),
};

// Weekly Planning API - Agent-specific endpoints
export const weeklyPlanning = {
  // Create new weekly plan
  create: (data) => api.post("/agent/weekly-plan/create", data),

  // Get current week's plan
  getCurrent: () => api.get("/agent/weekly-plan/current"),

  // Update existing plan
  update: (data) => api.put("/agent/weekly-plan/update", data),

  // Mark activity/event as completed
  markCompleted: (category, itemId, data = {}) =>
    api.put(`/agent/weekly-plan/complete/${category}/${itemId}`, data),

  // Activate a draft plan
  activate: (planId) => api.put(`/agent/weekly-plan/activate/${planId}`),

  // Get planning history
  getHistory: (params = {}) =>
    api.get("/agent/weekly-plan/history", { params }),

  // Get planning statistics
  getStats: (params = {}) => api.get("/agent/weekly-plan/stats", { params }),

  // Delete a plan
  delete: (planId) => api.delete(`/agent/weekly-plan/${planId}`),
};

// Weekly Reports API - Agent and Coach endpoints
export const weeklyReports = {
  // Agent endpoints
  agent: {
    // Create new weekly report
    create: (data) => api.post("/weekly-report/agent/create", data),

    // Submit weekly report
    submit: (reportId) => api.put(`/weekly-report/agent/submit/${reportId}`),

    // Get current week's report
    getCurrent: () => api.get("/weekly-report/agent/current"),

    // Get last week's report
    getLastWeek: () => api.get("/weekly-report/agent/last-week"),

    // Update report
    update: (reportId, data) =>
      api.put(`/weekly-report/agent/update/${reportId}`, data),

    // Get report history
    getHistory: (params = {}) =>
      api.get("/weekly-report/agent/history", { params }),
  },

  // Coach endpoints
  coach: {
    // Get all reports with filters
    getReports: (params = {}) =>
      api.get("/weekly-report/coach/reports", { params }),

    // Get agent performance metrics
    getAgentPerformance: (agentId, weeks = 4) =>
      api.get(`/weekly-report/coach/agent/${agentId}/performance`, {
        params: { weeks },
      }),

    // Review a report
    review: (reportId, data) =>
      api.put(`/weekly-report/coach/review/${reportId}`, data),

    // Approve a report
    approve: (reportId) => api.put(`/weekly-report/coach/approve/${reportId}`),
  },
};

// Task Progress API - Agent-specific task tracking
export const taskTracking = {
  // Get current week tasks for the authenticated agent
  getCurrentWeekTasks: () => api.get("/agent/tasks/current"),

  // Mark task as completed - UPDATED for unified dashboard
  markTaskCompleted: (taskId, data = {}) =>
    api.put(`/agent-task-assignments/tasks/${taskId}/complete`, data),

  // Mark task as uncompleted - UPDATED for unified dashboard
  markTaskUncompleted: (taskId) =>
    api.put(`/agent-task-assignments/tasks/${taskId}/uncomplete`, {}),

  // Unskip a skipped/opted-out task - UPDATED for unified dashboard
  unskipTask: (taskId) =>
    api.put(`/agent-task-assignments/tasks/${taskId}/unskip`, {}),

  // Opt out of a task with reason - UPDATED for unified dashboard
  optOutOfTask: (taskId, data) =>
    api.put(`/agent-task-assignments/tasks/${taskId}/opt-out`, data),

  // Mark task as viewed (for engagement tracking) - UPDATED for unified dashboard
  markTaskViewed: (taskId) =>
    api.put(`/agent-task-assignments/tasks/${taskId}/view`, {}),

  // Get overall progress analytics
  getProgress: () => api.get("/agent/tasks/progress"),

  // Get unified dashboard data with separated active/completed tasks
  getDashboard: () => api.get("/agent-task-assignments/dashboard"),

  // Get available opt-out reasons - UPDATED for unified dashboard
  getOptOutReasons: () => api.get("/agent-task-assignments/opt-out-reasons"),

  // Timer functionality - UPDATED for unified dashboard
  startTimer: (taskId) =>
    api.post(`/agent-task-assignments/tasks/${taskId}/start-timer`, {}),
  stopTimer: (taskId, data) =>
    api.post(`/agent-task-assignments/tasks/${taskId}/stop-timer`, data),
};

// Gamification API - Agent-specific endpoints
export const gamification = {
  // Get agent's gamification profile (points, level, streaks, badges)
  getProfile: () => api.get("/agent/gamification/profile"),

  // Get leaderboard rankings
  getLeaderboard: (timeframe = "weekly") =>
    api.get("/agent/gamification/leaderboard", { params: { timeframe } }),

  // Get available achievements and earned badges
  getAchievements: () => api.get("/agent/gamification/achievements"),

  // Trigger celebrations for level-ups and badge unlocks
  celebrate: (data) => api.post("/agent/gamification/celebrate", data),

  // Get detailed statistics
  getStats: () => api.get("/agent/gamification/stats"),

  // Manual point awards (if needed for admin)
  awardPoints: (data) => api.post("/agent/gamification/award-points", data),
};

// Feedback API
export const feedback = {
  // Submit feedback (bug, feature idea, or general feedback)
  submit: (data) => api.post("/feedback", data),
};

// Agent Resources API - Coach-managed resources for agents
export const agentResources = {
  // Get all resources with optional filtering (coach view)
  getAll: (params = {}) => api.get("/agent-resources", { params }),

  // Get single resource by ID
  getById: (id) => api.get(`/agent-resources/${id}`),

  // Get all categories (coach view)
  getCategories: () => api.get("/agent-resources/categories"),

  // Create new resource
  create: (data) => api.post("/agent-resources", data),

  // Update resource
  update: (id, data) => api.put(`/agent-resources/${id}`, data),

  // Archive resource (soft delete)
  archive: (id) => api.put(`/agent-resources/${id}/archive`),

  // Restore archived resource
  restore: (id) => api.put(`/agent-resources/${id}/restore`),

  // Permanently delete resource
  delete: (id) => api.delete(`/agent-resources/${id}`),

  // Record view (coach)
  recordView: (id) => api.put(`/agent-resources/${id}/view`),

  // Reorder resources
  reorder: (resources) => api.put("/agent-resources/batch/reorder", { resources }),

  // Agent-specific endpoints (for agent portal)
  agent: {
    // Get resources visible to agent
    getAll: (params = {}) => api.get("/agent-resources/agent/list", { params }),
    // Get single resource by ID for agent (for deep linking)
    getById: (id) => api.get(`/agent-resources/agent/${id}`),
    // Get categories visible to agent
    getCategories: () => api.get("/agent-resources/agent/categories"),
    // Record view by agent
    recordView: (id) => api.put(`/agent-resources/agent/${id}/view`),
  },
};

// Magic Links API (Temporary)
export const magicLinks = {
  // Generate a new temporary magic link for an agent
  generate: (email) => api.post("/auth/magic-link/generate", { email }),

  // Send magic link email
  sendEmail: (data) => api.post("/auth/magic-link/send-email", data),

  // Get magic link history for an agent
  getHistory: (agentId) => api.get(`/coach/magic-links/history/${agentId}`),
};

// Passwordless agent access. New sessions are established by httpOnly cookie;
// the browser never receives or stores a new session credential.
export const agentLogin = {
  request: (email, returnTo) =>
    api.post("/auth/agent-login/request", { email, returnTo }),
  exchange: (token) => api.post("/auth/agent-login/exchange", { token }),
  verifySession: () => api.post("/auth/agent/verify"),
  logout: () => api.post("/auth/agent-login/logout"),
  send: ({ agentIds, selection } = {}) =>
    api.post(
      "/auth/agent-login/send",
      agentIds ? { agentIds } : { selection }
    ),
  revokeSessions: (agentId) =>
    api.post("/auth/agent-login/revoke-sessions", { agentId }),
  setAccess: (agentId, active) =>
    api.put("/auth/agent-login/access", { agentId, active }),
};

// Agent-owned email changes require the existing httpOnly session cookie.
export const agentEmail = {
  get: () => api.get("/auth/agent-email"),
  requestChange: (email) => api.post("/auth/agent-email/change/request", { email }),
  confirmChange: (token) => api.post("/auth/agent-email/change/confirm", { token }),
  cancelChange: (requestId) => api.post("/auth/agent-email/change/cancel", { requestId }),
};

// Users API
export const users = {
  getAll: () => api.get("/auth/users"),
  // Get organization members for assignment dropdowns
  getOrgMembers: (params = {}) => api.get("/organization/members", { params }),
};

// Permanent Magic Links API
const getCoachAuthConfig = () => {
  // Auth token is now in httpOnly cookie
  // Just ensure credentials are sent with the request
  return {
    withCredentials: true,
  };
};

export const permanentMagicLinks = {
  // Cleanup-only controls for credentials issued before WP-1.9.
  revoke: (agentId) =>
    api.post("/auth/permanent-link/revoke", { agentId }, getCoachAuthConfig()),

  getStatus: (agentId) =>
    api.get(`/auth/permanent-link/status/${agentId}`, getCoachAuthConfig()),
};

// Agent Portal API
export const agentPortal = {
  // Resolve agent portal slug to redirect URL
  resolveSlug: (slug) => api.get(`/agent/portal/${slug}`),
};

// Task Plans API
export const taskPlans = {
  // Get all task plans with filtering and pagination
  getAll: (params = {}) => api.get("/task-plans", { params }),

  // Get specific task plan with weeks and assignments
  getById: (id, includeWeeks = true, includeAssignments = false) =>
    api.get(`/task-plans/${id}`, {
      params: { includeWeeks, includeAssignments },
    }),

  // Create new task plan
  create: (data) => api.post("/task-plans", data),

  // Update task plan
  update: (id, data) => api.put(`/task-plans/${id}`, data),

  // Archive/delete task plan
  delete: (id, reason) => api.delete(`/task-plans/${id}`, { data: { reason } }),

  // Duplicate task plan
  duplicate: (id, newName) =>
    api.post(`/task-plans/${id}/duplicate`, { newName }),

  // Assign coach-oriented plan to a specific user account
  assignToUser: (id, data) => api.post(`/task-plans/${id}/assign-to-user`, data),

  // Set as default onboarding plan
  setDefaultOnboarding: (id) =>
    api.put(`/task-plans/${id}/set-default-onboarding`),

  // Get plans suitable for specific agent
  getSuitableForAgent: (agentId) =>
    api.get(`/task-plans/templates/suitable-for-agent/${agentId}`),

  // Plan statistics and analytics
  getStats: (id) => api.get(`/task-plans/${id}/stats`),
  getAnalytics: (params = {}) => api.get("/task-plans/analytics", { params }),
};

// Task Plan Weeks API - REMOVED: Now using inline task templates in TaskPlan model

// Agent Task Plan Assignments API
export const agentTaskPlanAssignments = {
  // Get assignments with filtering
  getAll: (params = {}) => api.get("/agent-task-assignments", { params }),

  // Get assignments for specific plan
  getByPlan: (planId, params = {}) =>
    api.get(`/agent-task-assignments/plan/${planId}`, { params }),

  // Get assignments for specific agent
  getByAgent: (agentId, params = {}) =>
    api.get(`/agent-task-assignments/agent/${agentId}`, { params }),

  // Create new assignment
  create: (data) => api.post("/agent-task-assignments", data),

  // Bulk assign plans to agents
  bulkAssign: (data) => api.post("/agent-task-assignments/bulk-assign", data),

  // Update assignment
  update: (id, data) => api.put(`/agent-task-assignments/${id}`, data),

  // Cancel assignment
  cancel: (id, reason) =>
    api.delete(`/agent-task-assignments/${id}`, { data: { reason } }),

  // Manual controls
  pause: (id, reason = "Paused by coach") =>
    api.put(`/agent-task-assignments/${id}/pause`, { reason }),
  resume: (id) => api.put(`/agent-task-assignments/${id}/resume`),
  advance: (id) => api.put(`/agent-task-assignments/${id}/advance-week`),

  // Assignment analytics
  getProgress: (id) => api.get(`/agent-task-assignments/${id}/progress`),
  getAnalytics: (params = {}) =>
    api.get("/agent-task-assignments/analytics", { params }),
};

// Learning Paths API (Coach)
export const learningPaths = {
  // Get all learning paths for organization
  getAll: (params = {}) => api.get("/learning-paths", { params }),

  // Get single learning path with full details
  getById: (id) => api.get(`/learning-paths/${id}`),

  // Create new learning path
  create: (data) => api.post("/learning-paths", data),

  // Update learning path
  update: (id, data) => api.put(`/learning-paths/${id}`, data),

  // Archive learning path
  archive: (id) => api.put(`/learning-paths/${id}/archive`),

  // Restore archived learning path
  restore: (id) => api.put(`/learning-paths/${id}/restore`),

  // Delete learning path (permanent)
  delete: (id) => api.delete(`/learning-paths/${id}`),

  // Get class templates for attendance items
  getClassTemplates: (params = {}) => api.get("/learning-paths/classes/templates", { params }),

  // Get series templates for bulk import
  getSeriesTemplates: (params = {}) => api.get("/learning-paths/series/templates", { params }),

  // Import items from a series template
  importFromSeries: (seriesTemplateId) => api.post("/learning-paths/series/import", { seriesTemplateId }),

  // Assign learning path to agents
  assign: (id, agentIds) => api.post(`/learning-paths/${id}/assign`, { agentIds }),

  // Get all assignments for a learning path
  getAssignments: (id, params = {}) => api.get(`/learning-paths/${id}/assignments`, { params }),

  // Remove an assignment
  removeAssignment: (assignmentId) => api.delete(`/learning-paths/assignments/${assignmentId}`),

  // Progress dashboard
  getProgressDashboard: () => api.get("/learning-paths/progress/dashboard"),

  // Get detailed progress for an agent
  getAgentProgress: (agentId) => api.get(`/learning-paths/progress/agent/${agentId}`),

  // Evidence review
  getPendingReviews: () => api.get("/learning-paths/reviews/pending"),
  approveEvidence: (assignmentId, itemId) => api.put(`/learning-paths/reviews/${assignmentId}/approve`, { itemId }),
  rejectEvidence: (assignmentId, itemId, comment) => api.put(`/learning-paths/reviews/${assignmentId}/reject`, { itemId, comment }),
  coachOverride: (assignmentId, itemId, comment) => api.put(`/learning-paths/reviews/${assignmentId}/override`, { itemId, comment }),
};

// Agent Learning Paths API (Agent Portal)
export const agentLearningPaths = {
  // Get all assigned learning paths
  getAll: () => api.get("/agent/learning-paths"),

  // Get single assignment with full item details
  getById: (assignmentId) => api.get(`/agent/learning-paths/${assignmentId}`),

  // Get dashboard summary
  getSummary: () => api.get("/agent/learning-paths/summary/overview"),

  // Submit evidence for an item
  submitEvidence: (assignmentId, itemId, text) =>
    api.post(`/agent/learning-paths/${assignmentId}/items/${itemId}/submit-evidence`, { text }),

  // Mark self-reported item as complete
  markComplete: (assignmentId, itemId) =>
    api.post(`/agent/learning-paths/${assignmentId}/items/${itemId}/complete`),

  // Uncomplete a self-reported item
  uncomplete: (assignmentId, itemId) =>
    api.post(`/agent/learning-paths/${assignmentId}/items/${itemId}/uncomplete`),
};

// Referrals API
export const referrals = {
  // Get user's referrals with pagination
  list: (page = 1, limit = 10) => api.get("/referrals", { params: { page, limit } }),

  // Get referral statistics
  getStats: () => api.get("/referrals/stats"),

  // Get user's default testimonial
  getDefaultTestimonial: () => api.get("/referrals/default-testimonial"),

  // Update user's default testimonial
  updateDefaultTestimonial: (testimonial) => api.put("/referrals/default-testimonial", { testimonial }),

  // Send referral invitation
  send: (data) => api.post("/referrals/send", data),
};

export const agentSegments = {
  list: () => api.get("/settings/agent-segments"),
  create: (name, filters) =>
    api.post("/settings/agent-segments", { name, filters }),
  update: (id, data) => api.put(`/settings/agent-segments/${id}`, data),
  delete: (id) => api.delete(`/settings/agent-segments/${id}`),
};

export default api;
